import {
  CircularProgress,
  Stack,
  AutocompleteProps as MuiAutocompleteProps,
  Autocomplete,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import { useState, useEffect, useMemo, useRef } from "react";

import { MuiInput } from "./MuiInput";

export interface AsyncAutocompleteOption {
  value: string;
  label: string;
  type?: string;
  extra_information?: string | null;
  [x: string]: any;
}

interface AsyncAutocompleteProps extends Omit<
  MuiAutocompleteProps<AsyncAutocompleteOption, true, false, false>,
  "renderInput" | "options" | "onChange" | "value"
> {
  label: string;
  placeholder: string;
  fetchOptions: (query: string) => Promise<AsyncAutocompleteOption[]>;
  value: AsyncAutocompleteOption[];
  onChange: (value: AsyncAutocompleteOption[]) => void;
}

const filterOptions = (items: AsyncAutocompleteOption[], inputValue: string) =>
  items.filter((item) =>
    item.label.toLowerCase().includes(inputValue.toLowerCase()),
  );

const getUniqueOptions = (
  options: AsyncAutocompleteOption[],
): AsyncAutocompleteOption[] => {
  const seenByValue = new Set<string>();
  const result: AsyncAutocompleteOption[] = [];

  options.forEach((option) => {
    const key = `${option.type}-${option.value}`;
    if (seenByValue.has(key)) {
      return;
    }

    seenByValue.add(key);
    result.push(option);
  });

  return result;
};

export const AsyncAutocomplete = ({
  label,
  placeholder,
  fetchOptions,
  value,
  onChange,
  sx,
}: AsyncAutocompleteProps) => {
  const [options, setOptions] = useState<AsyncAutocompleteOption[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const isInitialized = useRef(false);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (input: string) => {
        try {
          setLoading(true);
          const results = await fetchOptions(input);
          const uniqueResults = getUniqueOptions(results);
          setOptions(uniqueResults);
          isInitialized.current = true;
        } catch (error) {
          console.error("Error fetching options:", error);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    [fetchOptions],
  );

  useEffect(() => {
    debouncedFetch(inputValue);

    return () => debouncedFetch.clear();
  }, [inputValue, debouncedFetch]);

  return (
    <Autocomplete
      disableCloseOnSelect
      filterSelectedOptions
      multiple
      filterOptions={(items) => filterOptions(items, inputValue)}
      getOptionKey={(option) => `${option.type}:${option.value}`}
      inputValue={inputValue}
      isOptionEqualToValue={(option, selected) =>
        option.value === selected.value
      }
      limitTags={1}
      loading={loading}
      noOptionsText={loading ? "Loading..." : "No options"}
      options={options}
      sx={{ ...sx }}
      value={value}
      getOptionLabel={(option) => {
        if (option.extra_information) {
          return `${option.label} (${option.extra_information})`;
        }

        return option.label;
      }}
      renderInput={(params) => (
        <MuiInput
          {...params}
          fullWidth
          label={label}
          placeholder={placeholder}
          size="medium"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <Stack
                    sx={{
                      position: "absolute",
                      top: "50%",
                      transform: "translate(0, -50%)",
                      right: 40,
                    }}>
                    <CircularProgress size={16} />
                  </Stack>
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      onChange={(_, newValue) => {
        onChange(newValue);
        setInputValue("");
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input" || reason === "clear") {
          setInputValue(newInputValue);
        }
      }}
    />
  );
};
